<template>
  <Provider v-if="canReadInspection || isTenant">
    <div
      class="inspection"
    >
      <Collapse
        class="inspection__collaspe"
        :title="$t('tenantProjects.inspection.title')"
      >
        <div
          slot="content"
          class="inspection__collaspe-content"
        >
          <HKLTable
            class="inspection__table"
            :dataSource="orderedItems"
            :columns="columns"
          />
          <HKLButton
            v-if="canUpdateInspection"
            color="darkblue"
            class="inspection__create-btn"
            @click="openNewInspectionModal"
          >
            {{ $t('tenantProjects.inspection.createButton') }}
          </HKLButton>
        </div>
      </Collapse>
      <EditInspectionModal
        ref="editPopup"
      />
    </div>
  </Provider>
</template>

<script>
import HKLButton from '@/controls/Button/HKLButton'
import Collapse from '@/controls/Collapse.vue'
import HKLTable from '@/controls/Table/HKLTable'
import EditInspectionModal from './edit.vue'
import { emptyValue } from '@/utils/consts'
import * as inspectionAPI from '@/api/inspection/request'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { useRouteParamId } from '@/router/useRouter'
import { useStoreGetters, useStoreActions } from '@/store/index'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import dateUtil from '@/utils/dateUtil'
import { computed, getCurrentInstance, onMounted, reactive, toRefs, unref } from 'vue-demi'
import Provider from './Provider'
import { ITEM_INSPECTION } from './consts'
import { useCanUpdateInspection, useItemInspectedOptions } from './hooks'
import inspectionMixin from './inspectionMixin'

export default {
  name: 'InspectionIndex',
  components: {
    HKLButton,
    Collapse,
    HKLTable,
    EditInspectionModal,
    Provider,
  },
  mixins: [inspectionMixin],
  setup () {
    const { proxy } = getCurrentInstance()
    const { isTenant } = useStoreGetters('user', ['isTenant'])
    const { inspections: inspectionData } = useStoreGetters('tenantProject', ['inspections'])
    const projectId = useRouteParamId()
    const { loadAllInspections } = useStoreActions('tenantProject', ['loadAllInspections'])
    const editModals = useRefModal()
    const canReadInspection = computed(() => aclHelper.getAccess(tmsPermsTypes.project.readInspection))
    const itemInspectionOptions = useItemInspectedOptions()
    const setupData = reactive({
      editPopup: editModals.modal,
      isTenant,
      inspectionData,
      canReadInspection,
      canUpdateInspection: useCanUpdateInspection(),
      disabledDates: computed(() => {
        return {
          to: new Date(Date.now()),
        }
      }),
      columns: computed(() => {
        const componentA = {
          name: 'a',
          attrs: {
            class: 'underline',
          },
          listeners: (_, record) => ({
            click: () => {
              editModals.modal.value.openCurrentRecord(record.raw)
            },
          }),
        }
        const result = [
          {
            title: proxy.$t('tenantProjects.inspection.type'),
            dataIndex: 'type',
            component: componentA,
            emptyValue: proxy.$t('emptyValue'),
          },
          {
            title: proxy.$t('tenantProjects.inspection.itemInspection'),
            dataIndex: 'itemInspection',
            component: componentA,
            emptyValue: proxy.$t('emptyValue'),
          },
          {
            title: proxy.$t('tenantProjects.inspection.dateTime'),
            dataIndex: 'date',
            component: componentA,
            emptyValue: proxy.$t('emptyValue'),
          },
          {
            title: proxy.$t('tenantProjects.inspection.descOfWork'),
            dataIndex: 'descOfWork',
            component: componentA,
            emptyValue: proxy.$t('emptyValue'),
          },
          {
            title: proxy.$t('tenantProjects.inspection.handledBy'),
            dataIndex: 'handledBy',
            component: componentA,
            emptyValue: proxy.$t('emptyValue'),
          },
          {
            title: proxy.$t('tenantProjects.inspection.tenantViewable'),
            dataIndex: 'isTenantView',
            component: {
              name: 'input',
              attrs: (value) => {
                const checked = value || undefined
                const disabled = !setupData.canUpdateInspection || undefined
                return Object.assign({ type: 'checkbox' }, { checked, disabled })
              },
              listeners: (_, record) => ({
                click: async () => {
                  const tenantViewable = !record.isTenantView
                  await inspectionAPI.updateTenantViewable(record.raw.id, tenantViewable)
                  await loadAllInspections(projectId.value)
                },
              }),
            },
          },
        ]
        isTenant.value && result.pop()
        return result
      }),
      orderedItems: computed(() => {
        return inspectionData.value.map(item => {
          const itemInspectionContent = [unref(itemInspectionOptions)[item.itemInspection] || emptyValue]
          if (item.itemInspection === ITEM_INSPECTION.Others && item.additionalInfo) {
            itemInspectionContent.push(item.additionalInfo)
          }
          return {
            ...item,
            date: dateUtil.format(item.date, 'DD MMM YY, HH:mm'),
            itemInspection: itemInspectionContent.join(' - '),
            type: proxy.customInsType(item.type),
            handledBy: item.handledBy,
          }
        })
      }),
      openNewInspectionModal: () => {
        editModals.modal.value.createNewRecord()
      },
    })
    onMounted(async () => {
      if (isTenant.value || canReadInspection.value) {
        await loadAllInspections(projectId.value)
      }
    })
    return {
      ...toRefs(setupData),
    }
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/textField';
.inspection {
  margin-top: 20px;
  &__table {
    width: 100%;
    margin: 0;
  }
  &__collaspe {
    &-content {
      padding: 15px;
      display: flex;
      align-items: flex-start;
    }
    &-status {
      color: #3ba04d;
      margin-right: 30px;
    }
    &-status_check {
      width: 20px;
      height: 20px;
      line-height: 20px;
      border-radius: 50%;
      background-color: #3ba04d;
      color: #ffffff;
      text-align: center;
      margin-right: 10px;
    }
  }
  &__create-btn {
    flex: 1 1 130px;
    margin-left: 10px;
  }
}

</style>
